import ChevronLeftSvg from "@/src/assets/icons/icon-chevron-left-black.svg";
import { ReactComponent as CloseSvg } from "@/src/assets/icons/icon-close-red.svg";
import { ReactComponent as PencelSvg } from "@/src/assets/icons/icon-edit-pencel.svg";
import EyeSvg from "@/src/assets/icons/icon-eye-black.svg";
import EyeOffSvg from "@/src/assets/icons/icon-eye-off-red2.svg";
import InfoSvg from "@/src/assets/icons/icon-info-gray6.svg";
import PreviewSvg from "@/src/assets/icons/icon-preview-black.svg";
import AlertDialog from "@/src/components/atom/AlertDialog";
import { Button, IconButton } from "@/src/components/atom/Button";
import Checkbox from "@/src/components/atom/Checkbox";
import DatePicker from "@/src/components/atom/DatePicker";
import Icon from "@/src/components/atom/Icon";
import Input from "@/src/components/atom/Input";
import NumberInput from "@/src/components/atom/Input/NumberInput";
import Loader from "@/src/components/atom/Loader";
import Radio from "@/src/components/atom/Radio";
import RadioGroup from "@/src/components/atom/RadioGroup";
import Select from "@/src/components/atom/Select";
import TextArea from "@/src/components/atom/TextArea";
import Typo from "@/src/components/atom/Typo";
import BottomFixedContainer from "@/src/components/molecule/BottomFixedContainer";
import SectionCard from "@/src/components/molecule/SectionCard";
import SectionCardRow from "@/src/components/molecule/SectionCardRow";
import SingleImageUpload from "@/src/components/molecule/SingleImageUpload";
import ExporterMainLayout from "@/src/components/template/Layout/exporter/ExporterMainLayout";
import PlContent from "@/src/components/template/pdfs/Pl/PlContent";
import PlPreview from "@/src/components/template/pdfs/Pl/Preview";
import useAlert from "@/src/hooks/useAlert";
import useContentLoading from "@/src/hooks/useContentLoading";
import EXPORTER_PRIVATE_PATH from "@/src/routes/exporter/path";
import { useGetSessionQuery } from "@/src/store/apis/auth";
import { useGetCommonCodeViaCodeNameQuery } from "@/src/store/apis/common";
import { useUploadBigFileMutation } from "@/src/store/apis/media";
import {
  useCreateFinalPlMutation,
  useCreateTemporaryPlMutation,
  useLazyGetInitialPlInformationQuery,
  useLazyGetPlInformationQuery,
} from "@/src/store/apis/shipments/pl";
import {
  GeneratePlDto,
  KeyOfPlInfoDto,
  PlInfoDto,
  PlItemDto,
} from "@/src/store/apis/shipments/pl/interface";
import colorSet, { ColorType } from "@/src/styles/color";
import typo from "@/src/styles/typography";
import { addZeroPrefix } from "@/src/utils/addZeroPrefix";
import { commonCodeMapper } from "@/src/utils/commonCodeHelper";
import { isNull, isUndefined } from "@/src/utils/is";
import { pdf } from "@react-pdf/renderer";
import { ConfigProvider } from "antd";
import dayjs from "dayjs";
import { Fragment, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { css, styled } from "styled-components";
import { plDefaultValue } from "./constant/defaultValue";
import { commonPlApiFieldKey } from "./constant/plHelper";
import { ShipmentSelectTabState } from "./detail";
import { renderDocumentStatusBadge } from "./utils/renderShipmentStatusBadge";
import DatePickerNoticeTypo from "@/src/components/molecule/DatePickerNoticeTypo";

type OptionType = {
  value: string;
  label: string;
};

enum AlertDialogState {
  NULL,
  BACK_TO_PREVIOUS,
  DOCUMENT_INIT,
}

const ExporterEditPlPage = () => {
  const { t } = useTranslation();
  const params = useParams();
  const alert = useAlert();
  const id = Number(params.id);
  const navigate = useNavigate();
  const { handleContentLoadingOn, handleContentLoadingOff } =
    useContentLoading();
  const previewButtonRef = useRef<HTMLButtonElement>(null);
  const previewPdfResourceRef = useRef<PlInfoDto>(plDefaultValue);

  const [, force] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSignatureMediaId, setSelectedSignatureMediaId] =
    useState<number>();
  const [plInfo, setPlInfo] = useState<PlInfoDto>(plDefaultValue);
  const [noMarkItemsList, setNoMarksItemsList] = useState<number[]>([]);
  const [alertDialogState, setAlertDialogState] = useState<AlertDialogState>(
    AlertDialogState.NULL,
  );

  const {
    currentData: unitQuantityList,
    isFetching: isUnitQuantityListFetching,
  } = useGetCommonCodeViaCodeNameQuery({ codeName: "UNIT_QUANTITY" });

  const [getPlInfo, plInfoResult] = useLazyGetPlInformationQuery();
  const [getIntialPlInfo, initialPlInfoResult] =
    useLazyGetInitialPlInformationQuery();
  const [createTempPl] = useCreateTemporaryPlMutation();
  const [createFinalPl] = useCreateFinalPlMutation();
  const [uploadFile] = useUploadBigFileMutation();
  const { isManager } = useGetSessionQuery(undefined, {
    refetchOnMountOrArgChange: true,
    selectFromResult: ({ currentData, isError, isFetching }) => {
      const isUnstable = isError || isFetching || isUndefined(currentData);
      const isStable = !isUnstable;

      const isManager =
        currentData?.row.exporterUserType === "MANAGER" ||
        currentData?.row.exporterUserType === "MIDDLE_MANAGER";

      return {
        isManager: isStable ? isManager : true,
      };
    },
  });

  const isAllNetWeightHidden = plInfo.plItems
    .reduce<any[]>((acc, val) => {
      return [...acc, ...val.plItemContainers];
    }, [])
    .some((item) => item.isNetWeightHidden === true);

  const isAllGrossWeightHidden = plInfo.plItems
    .reduce<any[]>((acc, val) => {
      return [...acc, ...val.plItemContainers];
    }, [])
    .some((item) => item.isGrossWeightHidden === true);

  const isAllCbmHidden = plInfo.plItems
    .reduce<any[]>((acc, val) => {
      return [...acc, ...val.plItemContainers];
    }, [])
    .some((item) => item.isCbmHidden === true);

  const unitQuantityListToOptionList = unitQuantityList
    ? unitQuantityList.reduce<OptionType[]>(commonCodeMapper, [])
    : [];

  const totalOfContainer = (resource: PlItemDto[]) =>
    resource.reduce<{
      quantityUnit: string[];
      quantity: number;
      grossWeight: number;
      grossWeightUnit: string;
      netWeight: number;
      netWeightUnit: string;
      cbm: number;
      isGrossWeightHidden: boolean;
      isNetWeightHidden: boolean;
      isCbmHidden: boolean;
    }>(
      (acc, val) => {
        const totalOfsingleContainer = val.plItemContainers.reduce<{
          quantityUnit: string[];
          quantity: number;
          grossWeight: number;
          grossWeightUnit: string;
          netWeight: number;
          netWeightUnit: string;
          cbm: number;
          isGrossWeightHidden: boolean;
          isNetWeightHidden: boolean;
          isCbmHidden: boolean;
        }>(
          (a, v) => {
            return {
              quantityUnit: v.quantityUnit
                ? a.quantityUnit.includes(v.quantityUnit)
                  ? a.quantityUnit
                  : a.quantityUnit.concat(v.quantityUnit)
                : a.quantityUnit,
              quantity: a.quantity + (v?.quantity || 0),
              grossWeight:
                a.grossWeight +
                (Number.isNaN(Number(v?.grossWeight))
                  ? 0
                  : Number(v?.grossWeight)),
              grossWeightUnit: v.grossWeightUnit,
              netWeight:
                a.netWeight +
                (Number.isNaN(Number(v?.netWeight)) ? 0 : Number(v?.netWeight)),
              netWeightUnit: v.netWeightUnit,
              cbm: a.cbm + (isNull(v?.cbm) ? 0 : Number(v?.cbm) || 0),
              isGrossWeightHidden: a.isGrossWeightHidden
                ? true
                : v.isGrossWeightHidden,
              isNetWeightHidden: a.isNetWeightHidden
                ? true
                : v.isNetWeightHidden,
              isCbmHidden: a.isCbmHidden ? true : v.isCbmHidden,
            };
          },
          {
            quantityUnit: [],
            quantity: 0,
            grossWeight: 0,
            grossWeightUnit: "",
            netWeight: 0,
            netWeightUnit: "",
            cbm: 0,
            isGrossWeightHidden: false,
            isNetWeightHidden: false,
            isCbmHidden: false,
          },
        );

        return {
          quantityUnit: [
            ...acc.quantityUnit,
            ...totalOfsingleContainer.quantityUnit,
          ],
          quantity: acc.quantity + totalOfsingleContainer.quantity,
          grossWeight: acc.grossWeight + totalOfsingleContainer.grossWeight,
          grossWeightUnit: totalOfsingleContainer.grossWeightUnit,
          netWeight: acc.netWeight + totalOfsingleContainer.netWeight,
          netWeightUnit: totalOfsingleContainer.netWeightUnit,
          cbm: acc.cbm + totalOfsingleContainer.cbm,
          isGrossWeightHidden: acc.isGrossWeightHidden
            ? true
            : totalOfsingleContainer.isGrossWeightHidden,
          isNetWeightHidden: acc.isNetWeightHidden
            ? true
            : totalOfsingleContainer.isNetWeightHidden,
          isCbmHidden: acc.isCbmHidden
            ? true
            : totalOfsingleContainer.isCbmHidden,
        };
      },
      {
        quantityUnit: [],
        quantity: 0,
        grossWeight: 0,
        grossWeightUnit: "",
        netWeight: 0,
        netWeightUnit: "",
        cbm: 0,
        isGrossWeightHidden: false,
        isNetWeightHidden: false,
        isCbmHidden: false,
      },
    );

  const handleChange = (key: KeyOfPlInfoDto, value: any) => {
    setPlInfo((prev) => ({ ...prev, [key]: value }));
  };

  const handleChangeWithName = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setPlInfo((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleToggle = (key: KeyOfPlInfoDto) => {
    setPlInfo((prev) => ({ ...prev, [key]: !plInfo[key] }));
  };

  const handleClear = (key: KeyOfPlInfoDto) => {
    setPlInfo((prev) => ({ ...prev, [key]: "" }));
  };

  const handlePlIntialize = () => {
    if (!initialPlInfoResult.isSuccess || initialPlInfoResult.isFetching) {
      setPlInfo(plDefaultValue);
    } else {
      setPlInfo(initialPlInfoResult.currentData);
    }
  };

  const handleCreateCi = async (type: "temp" | "final") => {
    try {
      handleContentLoadingOn();
      previewPdfResourceRef.current = plInfo;
      const blob = await pdf(
        <PlContent
          data={{
            ...previewPdfResourceRef.current,
            totalQuantityUnit: totalOfContainer(
              previewPdfResourceRef.current.plItems,
            ).quantityUnit,
            totalQuantity: totalOfContainer(
              previewPdfResourceRef.current.plItems,
            ).quantity,
            totalGrossWeight: totalOfContainer(
              previewPdfResourceRef.current.plItems,
            ).grossWeight,
            totalGrossWeightUnit: totalOfContainer(
              previewPdfResourceRef.current.plItems,
            ).grossWeightUnit,
            totalNetWeight: totalOfContainer(
              previewPdfResourceRef.current.plItems,
            ).netWeight,
            totalNetWeightUnit: totalOfContainer(
              previewPdfResourceRef.current.plItems,
            ).netWeightUnit,
            totalCbm: totalOfContainer(previewPdfResourceRef.current.plItems)
              .cbm,
            totalIsGrossWeightHidden: totalOfContainer(
              previewPdfResourceRef.current.plItems,
            ).isGrossWeightHidden,
            totalIsNetWeightHidden: totalOfContainer(
              previewPdfResourceRef.current.plItems,
            ).isNetWeightHidden,
            totalIsCbmHidden: totalOfContainer(
              previewPdfResourceRef.current.plItems,
            ).isCbmHidden,
          }}
        />,
      ).toBlob();
      const ciPdfFile = new File([blob], "pl.pdf", { type: "application/pdf" });
      const uploadedFile = await uploadFile({
        files: [ciPdfFile],
        folder: "commons/",
      });
      const commonBody = Object.fromEntries(
        Object.entries(plInfo).filter(([key]) =>
          commonPlApiFieldKey.includes(key),
        ),
      ) as Omit<GeneratePlDto, "businessLogoMediaId">;
      const fetcher = type === "temp" ? createTempPl : createFinalPl;
      await fetcher({
        id,
        ...commonBody,
        plItems: plInfo.plItems.map(
          ({
            item,
            isItemHidden,
            hsCode,
            isHsCodeHidden,
            descriptionOfGoods,
            isDescriptionOfGoodsHidden,
            contractId,
            shippingMarks,
            isShippingMarksNoMarks,
            isShippingMarksHidden,
            plItemContainers,
          }) => ({
            item,
            isItemHidden,
            hsCode,
            isHsCodeHidden,
            descriptionOfGoods,
            isDescriptionOfGoodsHidden,
            contractId,
            shippingMarks,
            isShippingMarksNoMarks,
            isShippingMarksHidden,
            plItemContainers: plItemContainers.map(
              ({
                quantity,
                quantityUnit,
                quantityUnitCodeItemName,
                isGrossWeightHidden,
                isNetWeightHidden,
                cbm,
                isCbmHidden,
                containerId,
                grossWeight,
                netWeight,
              }) => ({
                quantity: quantity ? Number(quantity) : null,
                quantityUnit,
                quantityUnitCodeItemName,
                isGrossWeightHidden,
                isNetWeightHidden,
                cbm: cbm ? Number(cbm) : null,
                isCbmHidden,
                containerId,
                grossWeight: grossWeight ? Number(grossWeight) : null,
                netWeight: netWeight ? Number(netWeight) : null,
              }),
            ),
          }),
        ),
        simpleSignatureMedias: plInfo.simpleSignatureMedias
          ?.filter((media) => media)
          ?.map(({ id }) => ({
            id,
            isDefault: selectedSignatureMediaId === id,
          })),
        businessLogoMediaId: plInfo.businessLogoSimpleMedia?.id,
        plMediaId:
          type === "temp"
            ? undefined
            : "data" in uploadedFile
              ? uploadedFile.data[0].id
              : undefined,
        plTemporaryMediaId:
          type === "temp"
            ? "data" in uploadedFile
              ? uploadedFile.data[0].id
              : undefined
            : undefined,
      }).unwrap();

      navigate(
        `${EXPORTER_PRIVATE_PATH.SHIPMENT_DETAIL}/${id}?selectTab=${ShipmentSelectTabState.DOCUMENT_MEMO}`,
      );
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;

      alert.showAlert({ type: "error", message });
    } finally {
      handleContentLoadingOff();
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const response = await getPlInfo({ id }).unwrap();
        console.log('[editPl] API Response:', {
          hasLogo: !!response.businessLogoSimpleMedia,
          logoUrl: response.businessLogoSimpleMedia?.mediaUrl,
          hasSignatures: !!response.simpleSignatureMedias,
          signatures: response.simpleSignatureMedias?.map(s => ({
            id: s.id,
            isDefault: s.isDefault,
            url: s.mediaUrl
          }))
        });
        setSelectedSignatureMediaId(
          response.simpleSignatureMedias?.find(({ isDefault }) => isDefault)
            ?.id,
        );
        setPlInfo(response);
      } catch (e: any) {
        const message = Array.isArray(e.data.message)
          ? e.data.message[0]
          : e.data.message;

        alert.showAlert({ message, type: "error" });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync previewPdfResourceRef with plInfo updates
  useEffect(() => {
    console.log('[editPl] plInfo updated, syncing previewPdfResourceRef:', {
      hasLogo: !!plInfo.businessLogoSimpleMedia,
      logoUrl: plInfo.businessLogoSimpleMedia?.mediaUrl,
      hasSignatures: !!plInfo.simpleSignatureMedias,
      signatures: plInfo.simpleSignatureMedias?.map(s => ({
        id: s.id,
        isDefault: s.isDefault,
        url: s.mediaUrl
      }))
    });
    previewPdfResourceRef.current = plInfo;
  }, [plInfo]);

  useEffect(() => {
    (async () => {
      try {
        await getIntialPlInfo({ id });
      } catch (e: any) {
        const message = Array.isArray(e.data.message)
          ? e.data.message[0]
          : e.data.message;

        alert.showAlert({ message, type: "error" });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (plInfoResult.isFetching) {
      return handleContentLoadingOn();
    }

    setTimeout(() => {
      handleContentLoadingOff();
    }, 500);
  }, [
    plInfoResult.isFetching,
    handleContentLoadingOff,
    handleContentLoadingOn,
  ]);

  const renderPlInformation = () => {
    return (
      <>
        <RowSection>
          <HalfWidthContainer>
            <FlexColumnContainer>
              <SectionItemTitle>
                {t("shipment:exporter.document.label.businessLogo")}
              </SectionItemTitle>
              <BusinessLogoContainer>
                <SingleImageUpload
                  disabled={isManager}
                  onChange={(media) => {
                    handleChange("businessLogoSimpleMedia", media);
                  }}
                  value={
                    plInfo.businessLogoSimpleMedia
                      ? plInfo.businessLogoSimpleMedia.mediaUrl
                      : undefined
                  }
                  topAccessory={({ handleEdit, handleRemove }) => {
                    if (isManager) {
                      return null;
                    }

                    return (
                      <IconButtonContainer>
                        <StyledIconButton
                          buttonColor="black"
                          buttonGrade="tertiary"
                          buttonSize={24}
                          onClick={() => handleEdit()}
                          type="button"
                          disabled={isManager}
                        >
                          <PencelIcon color={isManager ? "gray8" : "gray2"} />
                        </StyledIconButton>
                        <StyledIconButton
                          buttonColor="black"
                          buttonGrade="tertiary"
                          buttonSize={24}
                          type="button"
                          disabled={isManager}
                          onClick={() => {
                            handleChange("businessLogoSimpleMedia", undefined);
                            handleRemove();
                          }}
                        >
                          <CloseIcon color={isManager ? "gray8" : "red2"} />
                        </StyledIconButton>
                      </IconButtonContainer>
                    );
                  }}
                />
              </BusinessLogoContainer>
              <FlexAlignCenter>
                <Icon
                  iconSrc={InfoSvg}
                  iconSize={16}
                  className="flex-shrink-0"
                />
                <Typo typoType="b9r" color="gray6">
                  {t("shipment:exporter.document.description.businessLogoFile")}
                </Typo>
              </FlexAlignCenter>
            </FlexColumnContainer>
          </HalfWidthContainer>
          <HalfWidthContainer>
            <FlexColumnContainer>
              <SectionItemTitle>
                {t("shipment:exporter.document.label.documentType")}
              </SectionItemTitle>

              <RadioGroup
                size="large"
                name="shipmentDocumentType"
                value={plInfo.shipmentDocumentType}
                onChange={(e) => {
                  handleChange("shipmentDocumentType", e.target.value);
                }}
                typoType="b7r"
                options={[
                  { label: "Original", value: "ORIGINAL" },
                  { label: "Draft", value: "DRAFT" },
                  { label: "Copy", value: "COPY" },
                ]}
              />
            </FlexColumnContainer>
          </HalfWidthContainer>
        </RowSection>

        <Divider colGap={24} />

        <InfoBox>
          <ExampleButton
            buttonColor="black"
            buttonGrade="tertiary"
            buttonSize={24}
            aria-hidden
          >
            <Icon iconSrc={EyeSvg} iconSize={16} />
          </ExampleButton>

          <Typo typoType="b9r" color="gray6">
            {t("shipment:exporter.document.description.hideIcon")}
          </Typo>
        </InfoBox>

        <RowSection>
          <HalfWidthColumnDirection className="gap-40">
            <FlexColumnContainer>
              <TitleCustomInput
                name="shipperTitle"
                value={plInfo.shipperTitle || ""}
                onChange={handleChangeWithName}
                onClear={() => handleClear("shipperTitle")}
              />

              <StyledTextArea
                name="shipper"
                value={plInfo.shipper || ""}
                onChange={handleChangeWithName}
              />
            </FlexColumnContainer>
            <FlexColumnContainer>
              <TitleCustomInput
                name="consigneeTitle"
                value={plInfo.consigneeTitle || ""}
                onChange={handleChangeWithName}
                onClear={() => handleClear("consigneeTitle")}
              />

              <StyledTextArea
                name="consignee"
                value={plInfo.consignee || ""}
                onChange={handleChangeWithName}
              />
            </FlexColumnContainer>
            <PossibleHiddenItem>
              <FlexColumnContainer>
                <TitleCustomInput
                  name="notifyPartyTitle"
                  value={plInfo.notifyPartyTitle || ""}
                  onChange={handleChangeWithName}
                  onClear={() => handleClear("notifyPartyTitle")}
                />

                <StyledTextArea
                  name="notifyParty"
                  value={plInfo.notifyParty || ""}
                  onChange={handleChangeWithName}
                  disabled={plInfo.isNotifyPartySameAsConsignee}
                />

                <StyledCheckboxLabel>
                  <Checkbox
                    checked={plInfo.isNotifyPartySameAsConsignee}
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleChange("notifyParty", plInfo.consignee);
                      }
                      handleChange(
                        "isNotifyPartySameAsConsignee",
                        e.target.checked,
                      );
                    }}
                  />
                  <Typo typoType="b7r">
                    {t(
                      "shipment:exporter.document.checkboxLabel.sameConsignee",
                    )}
                  </Typo>
                </StyledCheckboxLabel>
              </FlexColumnContainer>

              <HiddenButton
                isHidden={plInfo.isNotifyPartyHidden}
                onClick={() => handleToggle("isNotifyPartyHidden")}
              />
            </PossibleHiddenItem>
          </HalfWidthColumnDirection>

          <HalfWidthColumnDirection className="gap-40">
            <FlexColumnContainer>
              <TitleCustomInput
                name="invoicoNoAndDateTitle"
                value={plInfo.invoicoNoAndDateTitle || ""}
                onChange={handleChangeWithName}
                onClear={() => handleClear("invoicoNoAndDateTitle")}
              />

              <FlexAlignCenter className="gap-8">
                <HalfInputWidthContainer>
                  <Input disabled value={plInfo.invoiceNo || ""} />
                </HalfInputWidthContainer>
                <HalfInputWidthContainer>
                  <ConfigProvider
                    theme={{
                      components: {
                        DatePicker: {
                          fontSize: 16,
                          lineHeight: 1.5,
                          fontWeightStrong: 400,
                        },
                      },
                    }}
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      allowClear
                      showToday={false}
                      value={
                        plInfo.invoiceDateAt
                          ? dayjs(plInfo.invoiceDateAt)
                          : undefined
                      }
                      onChange={(date) => {
                        if (!date) {
                          return handleChange("invoiceDateAt", null);
                        }
                        handleChange("invoiceDateAt", date?.toISOString());
                      }}
                      renderExtraFooter={() => (
                        <DatePickerNoticeTypo
                          text={t("common:datePickerFooterInvoiceDate")}
                        />
                      )}
                    />
                  </ConfigProvider>
                </HalfInputWidthContainer>
              </FlexAlignCenter>
            </FlexColumnContainer>
            <FlexColumnContainer>
              <FlexAlignStart>
                <PossibleHiddenItem>
                  <FlexColumnContainer>
                    <TitleCustomInput
                      name="poNoTitle"
                      value={plInfo.poNoTitle || ""}
                      onChange={handleChangeWithName}
                      onClear={() => handleClear("poNoTitle")}
                    />
                    {plInfo.poNoList?.map((value) => {
                      return <Input disabled value={value || ""} />;
                    })}
                  </FlexColumnContainer>

                  <HiddenButton
                    isHidden={plInfo.isPoNoHidden}
                    onClick={() => handleToggle("isPoNoHidden")}
                  />
                </PossibleHiddenItem>

                <PossibleHiddenItem>
                  <FlexColumnContainer>
                    <TitleCustomInput
                      name="scNoTitle"
                      value={plInfo.scNoTitle || ""}
                      onChange={handleChangeWithName}
                      onClear={() => handleClear("scNoTitle")}
                    />
                    {plInfo.scNoList.map((value) => {
                      return <Input disabled value={value || ""} />;
                    })}
                  </FlexColumnContainer>
                  <HiddenButton
                    isHidden={plInfo.isScNoHidden}
                    onClick={() => handleToggle("isScNoHidden")}
                  />
                </PossibleHiddenItem>
              </FlexAlignStart>
            </FlexColumnContainer>

            {!plInfo.isLcInfoHidden && (
              <FlexColumnContainer>
                <TitleCustomInput
                  name="lcNoAndDateTitle"
                  value={plInfo.lcNoAndDateTitle || ""}
                  onChange={handleChangeWithName}
                  onClear={() => handleClear("lcNoAndDateTitle")}
                />

                {plInfo.lcInfoList &&
                  plInfo.lcInfoList.map((_, idx) => {
                    return (
                      <FlexAlignStart key={idx.toString()}>
                        <HalfInputWidthContainer>
                          <Input
                            placeholder="Enter L/C No."
                            name={"lcInfoList"}
                            value={plInfo.lcInfoList[idx].lcNo || ""}
                            onChange={(e) => {
                              handleChange(
                                "lcInfoList",
                                plInfo.lcInfoList.map((lcData, lcIdx) => {
                                  if (lcIdx === idx) {
                                    return { ...lcData, lcNo: e.target.value };
                                  }
                                  return lcData;
                                }),
                              );
                            }}
                            onClear={() =>
                              handleChange(
                                "lcInfoList",
                                plInfo.lcInfoList.map((lcData, lcIdx) => {
                                  if (lcIdx === idx) {
                                    return { ...lcData, lcNo: "" };
                                  }
                                  return lcData;
                                }),
                              )
                            }
                          />
                        </HalfInputWidthContainer>
                        <HalfInputWidthContainer>
                          <ConfigProvider
                            theme={{
                              components: {
                                DatePicker: {
                                  fontSize: 16,
                                  lineHeight: 1.5,
                                  fontWeightStrong: 400,
                                },
                              },
                            }}
                          >
                            <DatePicker
                              style={{ width: "100%" }}
                              value={
                                plInfo.lcInfoList[idx].expireDateAt
                                  ? dayjs(plInfo.lcInfoList[idx].expireDateAt)
                                  : undefined
                              }
                              onChange={(date) => {
                                if (!date) {
                                  return handleChange(
                                    "lcInfoList",
                                    plInfo.lcInfoList.map((lcData, lcIdx) => {
                                      if (lcIdx === idx) {
                                        return {
                                          ...lcData,
                                          expireDateAt: null,
                                        };
                                      }
                                      return lcData;
                                    }),
                                  );
                                }

                                handleChange(
                                  "lcInfoList",
                                  plInfo.lcInfoList.map((lcData, lcIdx) => {
                                    if (lcIdx === idx) {
                                      return {
                                        ...lcData,
                                        expireDateAt: date?.toISOString(),
                                      };
                                    }
                                    return lcData;
                                  }),
                                );
                              }}
                            />
                          </ConfigProvider>
                        </HalfInputWidthContainer>
                      </FlexAlignStart>
                    );
                  })}
              </FlexColumnContainer>
            )}

            <PossibleHiddenItem>
              <FlexColumnContainer>
                <TitleCustomInput
                  name="remarkTitle"
                  value={plInfo.remarkTitle || ""}
                  onChange={handleChangeWithName}
                  onClear={() => handleClear("remarkTitle")}
                />

                <StyledTextArea
                  name="remark"
                  value={plInfo.remark || ""}
                  maxLength={500}
                  onChange={handleChangeWithName}
                />
              </FlexColumnContainer>

              <HiddenButton
                isHidden={plInfo.isRemarkHidden}
                onClick={() => handleToggle("isRemarkHidden")}
              />
            </PossibleHiddenItem>
          </HalfWidthColumnDirection>
        </RowSection>

        <Divider colGap={48} />

        <RowSection>
          <HalfWidthColumnDirection className="gap-40">
            <PossibleHiddenItem>
              <FlexColumnContainer>
                <TitleCustomInput
                  name="portOfLoadingTitle"
                  value={plInfo.portOfLoadingTitle || ""}
                  onChange={handleChangeWithName}
                  onClear={() => handleClear("portOfLoadingTitle")}
                />

                <Input
                  name="portOfLoading"
                  value={plInfo.portOfLoading || ""}
                  onChange={handleChangeWithName}
                  onClear={() => handleClear("portOfLoading")}
                />
              </FlexColumnContainer>

              <HiddenButton
                isHidden={plInfo.isPortOfLoadingHidden}
                onClick={() => handleToggle("isPortOfLoadingHidden")}
              />
            </PossibleHiddenItem>

            <PossibleHiddenItem>
              <FlexColumnContainer>
                <TitleCustomInput
                  name="portOfDischargeTitle"
                  value={plInfo.portOfDischargeTitle || ""}
                  onChange={handleChangeWithName}
                  onClear={() => handleClear("portOfDischargeTitle")}
                />

                <Input
                  name="portOfDischarge"
                  value={plInfo.portOfDischarge || ""}
                  onChange={handleChangeWithName}
                  onClear={() => handleClear("portOfDischarge")}
                />
              </FlexColumnContainer>

              <HiddenButton
                isHidden={plInfo.isPortOfDischargeHidden}
                onClick={() => handleToggle("isPortOfDischargeHidden")}
              />
            </PossibleHiddenItem>
          </HalfWidthColumnDirection>

          <HalfWidthColumnDirection className="gap-40">
            <PossibleHiddenItem>
              <FlexColumnContainer>
                <TitleCustomInput
                  name="placeOfDeliveryTitle"
                  value={plInfo.placeOfDeliveryTitle || ""}
                  onChange={handleChangeWithName}
                  onClear={() => handleClear("placeOfDeliveryTitle")}
                />

                <Input
                  name="placeOfDelivery"
                  value={plInfo.placeOfDelivery || ""}
                  onChange={handleChangeWithName}
                  onClear={() => handleClear("placeOfDelivery")}
                />
              </FlexColumnContainer>

              <HiddenButton
                isHidden={plInfo.isPlaceOfDeliveryHidden}
                onClick={() => handleToggle("isPlaceOfDeliveryHidden")}
              />
            </PossibleHiddenItem>
          </HalfWidthColumnDirection>
        </RowSection>

        <Divider colGap={48} />

        <RowSection>
          <HalfWidthColumnDirection className="gap-40">
            <PossibleHiddenItem>
              <FlexColumnContainer>
                <TitleCustomInput
                  value={plInfo.vesselAndVoyageNoTitle || ""}
                  disabled
                />

                <FlexAlignCenter className="gap-8">
                  <HalfInputWidthContainer>
                    <Input
                      name="vessel"
                      value={plInfo.vessel || ""}
                      onChange={handleChangeWithName}
                      onClear={() => handleClear("vessel")}
                    />
                  </HalfInputWidthContainer>
                  <HalfInputWidthContainer>
                    <Input
                      name="voyageNo"
                      value={plInfo.voyageNo || ""}
                      onChange={handleChangeWithName}
                      onClear={() => handleClear("voyageNo")}
                    />
                  </HalfInputWidthContainer>
                </FlexAlignCenter>
              </FlexColumnContainer>

              <HiddenButton
                isHidden={plInfo.isVesselAndVoyageNoHidden}
                onClick={() => handleToggle("isVesselAndVoyageNoHidden")}
              />
            </PossibleHiddenItem>

            <PossibleHiddenItem>
              <FlexColumnContainer>
                <TitleCustomInput
                  name="etdTitle"
                  value={plInfo.etdTitle || ""}
                  onChange={handleChangeWithName}
                  onClear={() => handleClear("etdTitle")}
                />

                <ConfigProvider
                  theme={{
                    components: {
                      DatePicker: {
                        fontSize: 16,
                        lineHeight: 1.5,
                        fontWeightStrong: 400,
                      },
                    },
                  }}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    allowClear
                    value={plInfo.etdAt ? dayjs(plInfo.etdAt) : undefined}
                    onChange={(date) => {
                      if (!date) {
                        return handleChange("etdAt", null);
                      }
                      handleChange("etdAt", date?.toISOString());
                    }}
                    showToday={false}
                    renderExtraFooter={() => (
                      <DatePickerNoticeTypo
                        text={t("common:datePickerFooterEtd")}
                      />
                    )}
                  />
                </ConfigProvider>
              </FlexColumnContainer>

              <HiddenButton
                isHidden={plInfo.isEtdHidden}
                onClick={() => handleToggle("isEtdHidden")}
              />
            </PossibleHiddenItem>
          </HalfWidthColumnDirection>

          <HalfWidthColumnDirection className="gap-40">
            <PossibleHiddenItem>
              <FlexColumnContainer>
                <TitleCustomInput
                  name="paymentTermsTitle"
                  value={plInfo.paymentTermsTitle || ""}
                  onChange={handleChangeWithName}
                  onClear={() => handleClear("paymentTermsTitle")}
                />

                <Input
                  name="paymentTerms"
                  value={plInfo.paymentTerms || ""}
                  onChange={handleChangeWithName}
                  onClear={() => handleClear("paymentTerms")}
                />
              </FlexColumnContainer>

              <HiddenButton
                isHidden={plInfo.isPaymentTermsHidden}
                onClick={() => handleToggle("isPaymentTermsHidden")}
              />
            </PossibleHiddenItem>
            <PossibleHiddenItem>
              <FlexColumnContainer>
                <TitleCustomInput
                  name="shippingTermsTitle"
                  value={plInfo.shippingTermsTitle || ""}
                  onChange={handleChangeWithName}
                  onClear={() => handleClear("shippingTermsTitle")}
                />

                <Input
                  name="shippingTerms"
                  value={plInfo.shippingTerms || ""}
                  onChange={handleChangeWithName}
                  onClear={() => handleClear("shippingTerms")}
                />
              </FlexColumnContainer>

              <HiddenButton
                isHidden={plInfo.isShippingTermsHidden}
                onClick={() => handleToggle("isShippingTermsHidden")}
              />
            </PossibleHiddenItem>
          </HalfWidthColumnDirection>
        </RowSection>
      </>
    );
  };

  const renderItem = (item: PlItemDto) => {
    const handleItemChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      setPlInfo((prev) => {
        const newPlItem = prev.plItems.map((plItem) =>
          plItem.contractId === item.contractId
            ? { ...plItem, [e.target.name]: e.target.value }
            : plItem,
        );

        return { ...prev, plItems: newPlItem };
      });
    };

    const handleItemClear = (key: string) => {
      setPlInfo((prev) => {
        const newPlItem = prev.plItems.map((plItem) =>
          plItem.contractId === item.contractId
            ? { ...plItem, [key]: "" }
            : plItem,
        );

        return { ...prev, plItems: newPlItem };
      });
    };

    const handleItemToggle = (key: string) => {
      setPlInfo((prev) => {
        const newPlItem = prev.plItems.map((plItem) =>
          plItem.contractId === item.contractId
            ? { ...plItem, [key]: !(plItem as any)[key] }
            : plItem,
        );

        return { ...prev, plItems: newPlItem };
      });
    };

    return (
      <>
        <RowSection>
          <HalfWidthColumnDirection className="gap-40">
            <PossibleHiddenItem>
              <FlexColumnContainer>
                <TitleCustomInput value={item.shippingMarksTitle} disabled />

                <StyledTextArea
                  name="shippingMarks"
                  value={item.shippingMarks || ""}
                  onChange={handleItemChange}
                  disabled={noMarkItemsList.includes(item.contractId)}
                />

                <StyledCheckboxLabel>
                  <Checkbox
                    onChange={(e) => {
                      if (e.target.checked) {
                        setNoMarksItemsList((prev) => [
                          ...prev,
                          item.contractId,
                        ]);

                        setPlInfo((prev) => {
                          const newPlItem = prev.plItems.map((plItem) =>
                            plItem.contractId === item.contractId
                              ? { ...plItem, shippingMarks: "No marks" }
                              : plItem,
                          );

                          return { ...prev, plItems: newPlItem };
                        });
                      } else {
                        setNoMarksItemsList((prev) =>
                          prev.filter(
                            (contractId) => contractId !== item.contractId,
                          ),
                        );
                      }
                    }}
                  />
                  <Typo typoType="b7r">No marks</Typo>
                </StyledCheckboxLabel>
              </FlexColumnContainer>

              <HiddenButton
                isHidden={item.isShippingMarksHidden}
                onClick={() => handleItemToggle("isShippingMarksHidden")}
              />
            </PossibleHiddenItem>
          </HalfWidthColumnDirection>
          <HalfWidthColumnDirection className="gap-40">
            <FlexColumnContainer>
              <FlexAlignCenter className="gap-24">
                <PossibleHiddenItem>
                  <FlexColumnContainer>
                    <TitleCustomInput value={item.itemTitle} disabled />

                    <Input
                      name="item"
                      value={item.item || ""}
                      onChange={handleItemChange}
                      onClear={() => handleItemClear("item")}
                    />
                  </FlexColumnContainer>
                  <HiddenButton
                    isHidden={item.isItemHidden}
                    onClick={() => handleItemToggle("isItemHidden")}
                  />
                </PossibleHiddenItem>

                <PossibleHiddenItem>
                  <FlexColumnContainer>
                    <TitleCustomInput value={item.hsCodeTitle} disabled />

                    <Input
                      name="hsCode"
                      value={item.hsCode || ""}
                      onChange={handleItemChange}
                      onClear={() => handleItemClear("hsCode")}
                    />
                  </FlexColumnContainer>
                  <HiddenButton
                    isHidden={item.isHsCodeHidden}
                    onClick={() => handleItemToggle("isHsCodeHidden")}
                  />
                </PossibleHiddenItem>
              </FlexAlignCenter>
            </FlexColumnContainer>
            <PossibleHiddenItem>
              <FlexColumnContainer>
                <TitleCustomInput
                  value={item.descriptionOfGoodsTitle}
                  disabled
                />

                <StyledTextArea
                  name="descriptionOfGoods"
                  value={item.descriptionOfGoods || ""}
                  maxLength={1000}
                  placeholder={t(
                    "shipment:exporter.document.placeholder.descriptionOfGoods",
                  )}
                  onChange={handleItemChange}
                />
              </FlexColumnContainer>

              <HiddenButton
                isHidden={item.isDescriptionOfGoodsHidden}
                onClick={() => handleItemToggle("isDescriptionOfGoodsHidden")}
              />
            </PossibleHiddenItem>
          </HalfWidthColumnDirection>
        </RowSection>

        {item.plItemContainers.map((container) => {
          const handleContainerClear = (
            name: string,
            value?: string | null | number,
          ) => {
            const newPlItem = plInfo.plItems.map((plItem) =>
              plItem.contractId === item.contractId
                ? {
                    ...plItem,
                    plItemContainers: plItem.plItemContainers.map(
                      (plItemContainer) =>
                        plItemContainer.containerId === container.containerId
                          ? {
                              ...plItemContainer,
                              [name]: value,
                            }
                          : plItemContainer,
                    ),
                  }
                : plItem,
            );

            setPlInfo((prev) => {
              return { ...prev, plItems: newPlItem };
            });
          };

          const handleContainerToggle = (name: string, value: any) => {
            const newPlItem = plInfo.plItems.map((plItem) => ({
              ...plItem,
              plItemContainers: plItem.plItemContainers.map(
                (plItemContainer) => ({
                  ...plItemContainer,
                  [name]: value,
                }),
              ),
            }));

            setPlInfo((prev) => {
              return { ...prev, plItems: newPlItem };
            });
          };

          return (
            <Fragment key={container.containerId}>
              <Divider colGap={48} />

              <RowSection>
                <HalfWidthColumnDirection className="gap-40">
                  <FlexColumnContainer>
                    <FlexAlignCenter className="gap-24">
                      <FlexColumnContainer>
                        <TitleCustomInput
                          value={container.containerNoTitle}
                          disabled
                        />

                        <FlexAlignStart className="gap-8">
                          <FlexColumnContainer>
                            <Input
                              disabled
                              value={container.containerNo || ""}
                            />
                          </FlexColumnContainer>
                        </FlexAlignStart>
                      </FlexColumnContainer>

                      <FlexColumnContainer>
                        <TitleCustomInput
                          value={container.sealNoTitle}
                          disabled
                        />

                        <FlexAlignStart className="gap-8">
                          <FlexColumnContainer>
                            <Input value={container.sealNo || ""} disabled />
                          </FlexColumnContainer>
                        </FlexAlignStart>
                      </FlexColumnContainer>
                    </FlexAlignCenter>
                  </FlexColumnContainer>
                </HalfWidthColumnDirection>
              </RowSection>

              <RowSection className="padding-top-40">
                <HalfWidthColumnDirection className="gap-40">
                  <FlexColumnContainer>
                    <FlexAlignCenter className="gap-24">
                      <FlexColumnContainer>
                        <TitleCustomInput
                          value={container.quantityTitle || ""}
                          disabled
                        />

                        <FlexAlignStart className="gap-8">
                          <FlexColumnContainer>
                            <NumberInput
                              value={
                                container.quantity
                                  ? Number(container.quantity)
                                  : null
                              }
                              onChange={(value) =>
                                handleContainerClear("quantity", value)
                              }
                            />

                            <Select
                              value={container.quantityUnit}
                              options={unitQuantityListToOptionList}
                              showSearch
                              placeholder="Search"
                              suffixIcon={
                                isUnitQuantityListFetching ? (
                                  <Loader />
                                ) : undefined
                              }
                              allowClear
                              disabled={isUnitQuantityListFetching}
                              onChange={(value: string, option: any) => {
                                if (!value) {
                                  handleContainerClear(
                                    "quantityUnitCodeItemName",
                                    null,
                                  );
                                  handleContainerClear("quantityUnit", null);
                                  return;
                                }
                                handleContainerClear(
                                  "quantityUnitCodeItemName",
                                  value,
                                );
                                handleContainerClear(
                                  "quantityUnit",
                                  option.label,
                                );
                              }}
                            />
                          </FlexColumnContainer>
                        </FlexAlignStart>
                      </FlexColumnContainer>

                      <FlexColumnContainer>
                        <PossibleHiddenItem>
                          <FlexColumnContainer>
                            <TitleCustomInput
                              value={container.grossWeightTitle || ""}
                              disabled
                            />

                            <NumberInput
                              value={
                                container.grossWeight &&
                                !Number.isNaN(container.grossWeight)
                                  ? Number(container.grossWeight)
                                  : null
                              }
                              onChange={(value) =>
                                handleContainerClear("grossWeight", value)
                              }
                            />
                            <Input
                              value={container.grossWeightUnit || ""}
                              disabled
                            />
                          </FlexColumnContainer>
                          <HiddenButton
                            isHidden={isAllGrossWeightHidden}
                            onClick={() =>
                              handleContainerToggle(
                                "isGrossWeightHidden",
                                !isAllGrossWeightHidden,
                              )
                            }
                          />
                        </PossibleHiddenItem>
                      </FlexColumnContainer>
                    </FlexAlignCenter>
                  </FlexColumnContainer>
                </HalfWidthColumnDirection>
                <HalfWidthColumnDirection className="gap-40">
                  <FlexColumnContainer>
                    <FlexAlignStart className="gap-24">
                      <FlexColumnContainer>
                        <PossibleHiddenItem>
                          <FlexColumnContainer>
                            <TitleCustomInput
                              value={container.netWeightTitle || ""}
                              disabled
                            />
                            <NumberInput
                              value={
                                container.netWeight &&
                                !Number.isNaN(container.netWeight)
                                  ? Number(container.netWeight)
                                  : null
                              }
                              onChange={(value) => {
                                handleContainerClear("netWeight", value);
                              }}
                            />
                            <Input
                              value={container.netWeightUnit || ""}
                              disabled
                            />
                          </FlexColumnContainer>
                          <HiddenButton
                            isHidden={isAllNetWeightHidden}
                            onClick={() =>
                              handleContainerToggle(
                                "isNetWeightHidden",
                                !isAllNetWeightHidden,
                              )
                            }
                          />
                        </PossibleHiddenItem>
                      </FlexColumnContainer>

                      <FlexColumnContainer>
                        <PossibleHiddenItem>
                          <FlexColumnContainer>
                            <TitleCustomInput
                              value={container.cbmTitle || ""}
                              disabled
                            />
                            <NumberInput
                              value={
                                container.cbm ? Number(container.cbm) : null
                              }
                              onChange={(value) =>
                                handleContainerClear("cbm", value)
                              }
                            />
                          </FlexColumnContainer>
                          <HiddenButton
                            isHidden={isAllCbmHidden}
                            onClick={() =>
                              handleContainerToggle(
                                "isCbmHidden",
                                !isAllCbmHidden,
                              )
                            }
                          />
                        </PossibleHiddenItem>
                      </FlexColumnContainer>
                    </FlexAlignStart>
                  </FlexColumnContainer>
                </HalfWidthColumnDirection>
              </RowSection>
            </Fragment>
          );
        })}
      </>
    );
  };

  const renderTotal = () => {
    return (
      <RowSection>
        <HalfWidthContainerWithDivider>
          <SectionCardRow
            label="No. of Container"
            value={[
              <Typo typoType="h7">{plInfo.numberOfContainerTypeA}</Typo>,
              <Typo typoType="h4">{plInfo.numberOfContainerTypeB}</Typo>,
            ]}
          />
        </HalfWidthContainerWithDivider>
        <ColDivider rowGap={1} />
        <HalfWidthContainerWithDivider>
          <FlexColumnContainer className="gap-16">
            <SectionCardRow
              label="Quantity"
              value={[
                <Typo typoType="b7r" color="gray5">
                  {totalOfContainer(plInfo.plItems).quantity.toLocaleString(
                    "ko-KR",
                  )}{" "}
                  {totalOfContainer(plInfo.plItems).quantityUnit.length === 0 ||
                  totalOfContainer(plInfo.plItems).quantityUnit.length > 1
                    ? "Packages"
                    : totalOfContainer(plInfo.plItems).quantityUnit[0]}
                </Typo>,
              ]}
            />
            {!totalOfContainer(plInfo.plItems).isGrossWeightHidden && (
              <SectionCardRow
                label="Gross Weight"
                value={[
                  <Typo typoType="b7r" color="gray5">
                    {totalOfContainer(
                      plInfo.plItems,
                    ).grossWeight.toLocaleString("ko-KR")}{" "}
                    {totalOfContainer(plInfo.plItems).grossWeightUnit}
                  </Typo>,
                ]}
              />
            )}
            {!totalOfContainer(plInfo.plItems).isNetWeightHidden && (
              <SectionCardRow
                label="Net Weight"
                value={[
                  <Typo typoType="b7r" color="gray5">
                    {totalOfContainer(plInfo.plItems).netWeight.toLocaleString(
                      "ko-KR",
                    )}{" "}
                    {totalOfContainer(plInfo.plItems).netWeightUnit}
                  </Typo>,
                ]}
              />
            )}
            {!totalOfContainer(plInfo.plItems).isCbmHidden && (
              <SectionCardRow
                label="CBM"
                value={[
                  <Typo typoType="b7r" color="gray5">
                    {totalOfContainer(plInfo.plItems).cbm.toLocaleString(
                      "ko-KR",
                    )}
                  </Typo>,
                ]}
              />
            )}
          </FlexColumnContainer>
        </HalfWidthContainerWithDivider>
      </RowSection>
    );
  };

  const renderEtc = () => {
    return (
      <RowSection>
        <HalfWidthColumnDirection className="gap-40">
          <FlexColumnContainer className="gap-16">
            <Typo typoType="h4">
              {t("shipment:exporter.document.label.selectSignature")}
            </Typo>

            <FlexAlignCenter>
              <SingleSignatureContainer>
                <SignatureDiv>
                  <SingleImageUpload
                    disabled={isManager}
                    onChange={(media, type) => {
                      handleChange("simpleSignatureMedias", [
                        media,
                        plInfo.simpleSignatureMedias?.[1],
                      ]);

                      if (
                        (!plInfo.simpleSignatureMedias?.[1] &&
                          type === "create") ||
                        (type === "edit" &&
                          plInfo.simpleSignatureMedias?.[0].id ===
                            selectedSignatureMediaId)
                      ) {
                        setSelectedSignatureMediaId(media.id);
                      }
                    }}
                    value={
                      plInfo.simpleSignatureMedias?.[0]?.mediaUrl || undefined
                    }
                    topAccessory={({ handleEdit, handleRemove }) => {
                      if (isManager) {
                        return null;
                      }

                      return (
                        <IconButtonContainer>
                          <StyledIconButton
                            buttonColor="black"
                            buttonGrade="tertiary"
                            buttonSize={24}
                            onClick={handleEdit}
                            type="button"
                            disabled={isManager}
                          >
                            <PencelIcon color={isManager ? "gray8" : "gray2"} />
                          </StyledIconButton>
                          <StyledIconButton
                            buttonColor="black"
                            buttonGrade="tertiary"
                            buttonSize={24}
                            type="button"
                            disabled={isManager}
                            onClick={() => {
                              if (plInfo.simpleSignatureMedias?.[1]) {
                                setSelectedSignatureMediaId(
                                  plInfo.simpleSignatureMedias[1].id,
                                );
                                handleChange("simpleSignatureMedias", [
                                  plInfo.simpleSignatureMedias[1],
                                  null,
                                ]);
                              } else {
                                handleChange("simpleSignatureMedias", [
                                  null,
                                  null,
                                ]);
                                setSelectedSignatureMediaId(undefined);
                              }
                              handleRemove();
                            }}
                          >
                            <CloseIcon color={isManager ? "gray8" : "red2"} />
                          </StyledIconButton>
                        </IconButtonContainer>
                      );
                    }}
                  />
                </SignatureDiv>

                <Radio
                  label={t(
                    "shipment:exporter.document.radioLabel.signatureOne",
                  )}
                  typoType="b7r"
                  checked={
                    plInfo.simpleSignatureMedias?.[0] &&
                    plInfo.simpleSignatureMedias[0].id ===
                      selectedSignatureMediaId
                  }
                  disabled={!plInfo.simpleSignatureMedias?.[0]}
                  onChange={() => {
                    setSelectedSignatureMediaId(
                      plInfo.simpleSignatureMedias?.[0].id,
                    );
                  }}
                />
              </SingleSignatureContainer>

              <SingleSignatureContainer>
                <SignatureDiv>
                  <SingleImageUpload
                    disabled={isManager}
                    onChange={(media, type) => {
                      if (!plInfo.simpleSignatureMedias?.[0]) {
                        handleChange("simpleSignatureMedias", [media, null]);
                        setSelectedSignatureMediaId(media.id);
                      } else {
                        handleChange("simpleSignatureMedias", [
                          plInfo.simpleSignatureMedias[0],
                          media,
                        ]);
                      }

                      if (
                        type === "edit" &&
                        plInfo.simpleSignatureMedias?.[1].id ===
                          selectedSignatureMediaId
                      ) {
                        setSelectedSignatureMediaId(media.id);
                      }
                    }}
                    value={
                      plInfo.simpleSignatureMedias?.[1]?.mediaUrl || undefined
                    }
                    topAccessory={({ handleEdit, handleRemove }) => {
                      if (isManager) {
                        return null;
                      }

                      return (
                        <IconButtonContainer>
                          <StyledIconButton
                            buttonColor="black"
                            buttonGrade="tertiary"
                            buttonSize={24}
                            onClick={() => handleEdit()}
                            type="button"
                            disabled={isManager}
                          >
                            <PencelIcon color={isManager ? "gray8" : "gray2"} />
                          </StyledIconButton>
                          <StyledIconButton
                            buttonColor="black"
                            buttonGrade="tertiary"
                            buttonSize={24}
                            type="button"
                            disabled={isManager}
                            onClick={() => {
                              handleChange("simpleSignatureMedias", [
                                plInfo.simpleSignatureMedias?.[0],
                                null,
                              ]);
                              if (!!plInfo.simpleSignatureMedias?.[0]) {
                                setSelectedSignatureMediaId(
                                  plInfo.simpleSignatureMedias[0].id,
                                );
                              }

                              handleRemove();
                            }}
                          >
                            <CloseIcon color={isManager ? "gray8" : "red2"} />
                          </StyledIconButton>
                        </IconButtonContainer>
                      );
                    }}
                  />
                </SignatureDiv>

                <Radio
                  label={t(
                    "shipment:exporter.document.radioLabel.signatureTwo",
                  )}
                  typoType="b7r"
                  checked={
                    plInfo.simpleSignatureMedias?.[1] &&
                    plInfo.simpleSignatureMedias[1].id ===
                      selectedSignatureMediaId
                  }
                  disabled={!plInfo.simpleSignatureMedias?.[1]}
                  onChange={() => {
                    setSelectedSignatureMediaId(
                      plInfo.simpleSignatureMedias?.[1].id,
                    );
                  }}
                />
              </SingleSignatureContainer>
            </FlexAlignCenter>
          </FlexColumnContainer>
        </HalfWidthColumnDirection>
      </RowSection>
    );
  };

  const renderAlertDialog = () => {
    if (alertDialogState === AlertDialogState.NULL) {
      return null;
    }

    if (alertDialogState === AlertDialogState.BACK_TO_PREVIOUS) {
      return (
        <AlertDialog
          open
          title={t("shipment:exporter.alertDialog.backToPrevious.title")}
          description={t(
            "shipment:exporter.alertDialog.backToPrevious.description",
          )}
          onOpenChange={() => setAlertDialogState(AlertDialogState.NULL)}
          onCancel={() => setAlertDialogState(AlertDialogState.NULL)}
          cancelText={t("common:cancel")}
          okText={t("common:ok")}
          onOk={() => navigate(-1)}
        />
      );
    }
    if (alertDialogState === AlertDialogState.DOCUMENT_INIT) {
      return (
        <AlertDialog
          open
          title={t("shipment:exporter.alertDialog.documentIntialization.title")}
          description={t(
            "shipment:exporter.alertDialog.documentIntialization.description",
          )}
          onOpenChange={() => setAlertDialogState(AlertDialogState.NULL)}
          onCancel={() => setAlertDialogState(AlertDialogState.NULL)}
          cancelText={t("common:cancel")}
          okText={t("common:ok")}
          onOk={() => {
            handlePlIntialize();
            setAlertDialogState(AlertDialogState.NULL);
          }}
        />
      );
    }
  };

  return (
    <ExporterMainLayout
      breadcrumb={[
        "...",
        t("sideNav:shipmentDetail"),
        t("shipment:exporter.document.title.editPL"),
      ]}
      customPageTitle={
        <FlexSpaceBetween style={{ padding: "16px 0" }}>
          <FlexAlignCenter className="gap-8">
            <Typo as="h2" typoType="h1" color="gray1">
              {t("shipment:exporter.document.title.editPL")}
            </Typo>
            {renderDocumentStatusBadge({
              type:
                plInfo.plStatus === "PL_TEMPORARY"
                  ? "TEMPORARY_REGISTER"
                  : plInfo.plStatus === "PL_FINAL"
                    ? "FINAL"
                    : "BEFORE_REGISTRATION",
              t,
              size: "L",
            })}
          </FlexAlignCenter>

          <Button
            buttonSize={32}
            buttonGrade="secondary"
            buttonColor="blue"
            onClick={() => setAlertDialogState(AlertDialogState.DOCUMENT_INIT)}
          >
            {t("shipment:exporter.document.button.documentInitialization")}
          </Button>
        </FlexSpaceBetween>
      }
    >
      <InnerLayoutContainer>
        <SectionCard
          cardTitle={t("shipment:exporter.document.title.plInformation")}
        >
          {renderPlInformation()}
        </SectionCard>
        {plInfo.plItems.map((item, index) => {
          return (
            <SectionCard
              cardTitle={`Item ${addZeroPrefix(index + 1)}`}
              key={item.contractId}
            >
              {renderItem(item)}
            </SectionCard>
          );
        })}
        <SectionCard cardTitle="Total">{renderTotal()}</SectionCard>
        <SectionCard cardTitle="Etc">{renderEtc()}</SectionCard>
        <BottomFixedContainer>
          <FlexSpaceBetween>
            <Button
              buttonGrade="tertiary"
              buttonColor="black"
              style={{ display: "flex", alignItems: "center", gap: "4px" }}
              onClick={() =>
                setAlertDialogState(AlertDialogState.BACK_TO_PREVIOUS)
              }
            >
              <Icon iconSrc={ChevronLeftSvg} iconSize={16} />
              {t("shipment:exporter.document.button.backToPrevious")}
            </Button>

            <SaveButtonContainer>
              <PlPreview
                data={{
                  ...previewPdfResourceRef.current,
                  etdAt: previewPdfResourceRef.current?.etdAt
                    ? dayjs(previewPdfResourceRef.current?.etdAt).format(
                        "YYYY-MM-DD",
                      )
                    : undefined,
                  lcInfoList: previewPdfResourceRef.current.lcInfoList
                    ? previewPdfResourceRef.current.lcInfoList.map(
                        ({ lcNo, expireDateAt }) => {
                          return {
                            lcNo,
                            expireDateAt:
                              dayjs(expireDateAt).format("YYYY-MM-DD"),
                          };
                        },
                      )
                    : [],
                  invoiceDateAt: previewPdfResourceRef.current?.invoiceDateAt
                    ? dayjs(
                        previewPdfResourceRef.current?.invoiceDateAt,
                      ).format("YYYY-MM-DD")
                    : undefined,
                  commonWeightUnit: totalOfContainer(
                    previewPdfResourceRef.current.plItems,
                  ).grossWeightUnit,
                  componyLogoUrl:
                    previewPdfResourceRef.current.businessLogoSimpleMedia
                      ?.mediaUrl,
                  // Use default signature (isDefault flag) for PDF display
                  signatureUrl:
                    previewPdfResourceRef.current.simpleSignatureMedias?.find(
                      ({ isDefault }) => isDefault,
                    )?.mediaUrl,
                  totalQuantityUnit: totalOfContainer(
                    previewPdfResourceRef.current.plItems,
                  ).quantityUnit,
                  totalQuantity: totalOfContainer(
                    previewPdfResourceRef.current.plItems,
                  ).quantity,
                  totalGrossWeight: totalOfContainer(
                    previewPdfResourceRef.current.plItems,
                  ).grossWeight,
                  totalGrossWeightUnit: totalOfContainer(
                    previewPdfResourceRef.current.plItems,
                  ).grossWeightUnit,
                  totalNetWeight: totalOfContainer(
                    previewPdfResourceRef.current.plItems,
                  ).netWeight,
                  totalNetWeightUnit: totalOfContainer(
                    previewPdfResourceRef.current.plItems,
                  ).netWeightUnit,
                  totalCbm: totalOfContainer(
                    previewPdfResourceRef.current.plItems,
                  ).cbm,
                  totalIsGrossWeightHidden: totalOfContainer(
                    previewPdfResourceRef.current.plItems,
                  ).isGrossWeightHidden,
                  totalIsNetWeightHidden: totalOfContainer(
                    previewPdfResourceRef.current.plItems,
                  ).isNetWeightHidden,
                  totalIsCbmHidden: totalOfContainer(
                    previewPdfResourceRef.current.plItems,
                  ).isCbmHidden,
                }}
                trigger={<HiddenPreviewButton ref={previewButtonRef} />}
              />

              <Button
                buttonGrade="tertiary"
                buttonColor="black"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
                isLoading={isLoading}
                onClick={() => {
                  previewPdfResourceRef.current = plInfo;
                  force({});
                  setIsLoading(true);
                  setTimeout(() => {
                    previewButtonRef.current?.click();
                    setIsLoading(false);
                  }, 1000);
                }}
              >
                <Icon iconSrc={PreviewSvg} iconSize={16} />{" "}
                {t("shipment:exporter.document.button.preview")}
              </Button>

              <Button
                buttonGrade="secondary"
                buttonColor="blue"
                onClick={() => handleCreateCi("temp")}
              >
                {t("shipment:exporter.document.button.temporaryRegister")}
              </Button>
              <Button
                buttonGrade="primary"
                style={{ width: 158 }}
                onClick={() => handleCreateCi("final")}
              >
                {t("shipment:exporter.document.button.finalRegister")}
              </Button>
            </SaveButtonContainer>
          </FlexSpaceBetween>
        </BottomFixedContainer>
      </InnerLayoutContainer>
      {renderAlertDialog()}
    </ExporterMainLayout>
  );
};

export default ExporterEditPlPage;

const InnerLayoutContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 72px;
`;

const InfoBox = styled.div`
  padding: 16px;
  background: ${colorSet.gray11};
  border-radius: 8px;
  margin-bottom: 24px;

  display: flex;
  gap: 8px;
  align-items: center;
`;

const ExampleButton = styled(IconButton)`
  transform: none;

  &:hover {
    transform: none;
  }
`;

const TitleCustomInput = styled(Input)`
  ${typo.h7}
`;

const RowSection = styled.section`
  display: flex;
  width: 100%;
  gap: 24px;

  &.padding-top-40 {
    padding-top: 40px;
  }
`;

const HalfWidthContainer = styled.div`
  width: calc(50% - 12px);
`;

const HalfInputWidthContainer = styled.div`
  width: calc(50% - 4px);
`;

const HalfWidthContainerWithDivider = styled.div`
  width: calc(50% - 12.5px);
`;

const HalfWidthColumnDirection = styled(HalfWidthContainer)`
  display: flex;
  flex-direction: column;

  &.gap-40 {
    gap: 40px;
  }
`;

const BusinessLogoContainer = styled.div`
  height: 142px;
`;

const StyledIconButton = styled(IconButton)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const IconButtonContainer = styled.div`
  display: flex;
  gap: 4px;
  margin: 4px 4px 0 0;
`;

const FlexColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;

  &.gap-16 {
    gap: 16px;
  }
`;

const FlexAlignCenter = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  .flex-shrink-0 {
    flex-shrink: 0;
  }

  &.gap-8 {
    gap: 8px;
  }

  &.gap-24 {
    gap: 24px;
  }
`;

const FlexAlignStart = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;

  &.gap-24 {
    gap: 24px;
  }
`;

const FlexSpaceBetween = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

const SectionItemTitle = styled.p`
  ${typo.h7};
  color: ${colorSet.gray2};
`;

const StyledTextArea = styled(TextArea)`
  height: 100px;
`;

const PossibleHiddenItem = styled.div`
  display: flex;
  width: 100%;
  gap: 8px;
  align-items: flex-start;
`;

const StyledCheckboxLabel = styled.label`
  display: flex;
  align-self: flex-start;
  align-items: center;
  cursor: pointer;
  gap: 8px;
`;

const SingleSignatureContainer = styled.div`
  width: calc(50% - 4px);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const SignatureDiv = styled.div`
  height: 128px;
  width: 100%;
`;

const Divider = styled.div<{ colGap?: number }>`
  width: 100%;
  height: 1px;
  background: ${colorSet.gray9};
  ${({ colGap }) =>
    colGap
      ? css`
          margin: ${colGap}px 0;
        `
      : css`
          margin: 24px 0;
        `};
`;

const ColDivider = styled.div<{ rowGap?: number }>`
  width: 1px;
  background: ${colorSet.gray9};
  ${({ rowGap }) =>
    rowGap
      ? css`
          margin: 0 ${rowGap}px;
        `
      : css`
          margin: 0 24px;
        `};
`;

const SaveButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  text-align: center;
`;

const HiddenButton = ({
  isHidden,
  onClick,
}: {
  isHidden: boolean;
  onClick?: () => void;
}) => {
  return (
    <IconButton buttonColor="black" buttonGrade="tertiary" onClick={onClick}>
      <Icon iconSrc={isHidden ? EyeOffSvg : EyeSvg} iconSize={20} />
    </IconButton>
  );
};

const HiddenPreviewButton = styled.button`
  position: absolute;
  width: 0;
  height: 0;
  padding: 0;
  overflow: hidden;
  border: 0;
`;

const PencelIcon = styled(PencelSvg)<{ color: ColorType }>`
  width: 16px;
  height: 16px;

  path {
    fill: ${({ color }) => colorSet[color]};
  }
`;

const CloseIcon = styled(CloseSvg)<{ color: ColorType }>`
  width: 16px;
  height: 16px;

  path {
    fill: ${({ color }) => colorSet[color]};
  }
`;
